import { useState, useEffect } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { Edit, Trash } from "react-feather";
import { getCartProductsByCustomerId } from "../../service/CartService";
import { useToast } from "../toast/ToastProvider";
import { fetchProductImage } from "../../service/ProductImageService";
import { fetchAllCustomerProduct } from "../../service/ProductService";
import { apiUrl } from "../../config"; 
import { formatINR } from "../../App";
import { getWishlistProductsByCustomerId } from "../../service/WishlistService";


const CustomerList = ({ customerData, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); 
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistAllItems, setWishlistAllItems] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);
  const [allCartItems, setAllCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();


  useEffect(() => {
    const fetchAllCartCounts = async () => {
      try {
        const counts = {};
        for (const customer of customerData) {
          const cartData = await getCartProductsByCustomerId(customer.id);
          counts[customer.id] = Array.isArray(cartData) ? cartData.length : 0;
        }
        setAllCartItems(counts);
      } catch (error) {
        console.error("Error fetching cart counts:", error);
      }
    };
    const fetchAllWishCounts = async () =>{
      try{
        const counted = {};
        for(const wish of customerData){
          const wishlistData = await getWishlistProductsByCustomerId(wish.id);
          counted[wish.id] = Array.isArray(wishlistData) ? wishlistData.length :0;
        }
        setWishlistAllItems(counted);
      }catch(error){
        console.error("Error fetching wishlist counts",error);
        
      }
    }

    if (customerData.length > 0) {
      fetchAllCartCounts();
      fetchAllWishCounts();
    }
  }, [customerData]);

  // Reset cart when modal closes
  useEffect(() => {
    if (!showModal) {
      setCartItems([]);
      setSelectedCustomer(null);
    }
  }, [showModal]);

  const handleWishlist = async (customerId) => {
    const customerWishlist = customerData.find(cus => cus.id === customerId);
    if (!customerWishlist) {
      toast("customer not found", "ERROR");
    }
    setLoading(true);
    try {
      setShowWishlist(true);
      setSelectedCustomer(customerWishlist);

      const [wishlist, productImageData, productsData] = await Promise.all([
        getWishlistProductsByCustomerId(customerId),
        fetchProductImage(),
        fetchAllCustomerProduct(),
      ]);
      if (!Array.isArray(wishlist) || wishlist.length === 0) {
        setWishlistItems([]);
        return;
      }
      const wishlistData = wishlist.map((wish) => {
        const productDetails = productsData.find((pro) => pro.id === wish.productId);
        const productImages = productImageData.filter((prod) => prod.productId === wish.productId);
        const firstImages = productImages.length > 0 ? productImages[0].imageUrl : null;

        return {
          ...wish,
          productName: productDetails.name || 'UnKnown',
          ornamentPrice: productDetails.ornamentPrice || 0,
          imageUrl: firstImages,
        }
      });
      setWishlistItems(wishlistData);
    } catch (error) {
      console.error("Error fetch wishlist data", error);
      toast('Fetching load wishlist data', 'ERROR');
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  }

  const handleViewCart = async (customerId) => {
    const customer = customerData.find(c => c.id === customerId);
    if (!customer) {
      toast("Customer not found", "ERROR");
      return;
    }

    setLoading(true);
    try {
      setShowModal(true);
      setSelectedCustomer(customer);

      const [cartData, productImageData, productsData] = await Promise.all([
        getCartProductsByCustomerId(customerId),
        fetchProductImage(),
        fetchAllCustomerProduct(),
      ]);

      if (!Array.isArray(cartData) || cartData.length === 0) {
        setCartItems([]);
        return;
      }

      const mergedData = cartData.map((cartItem) => {
        const productDetails = productsData.find(p => p.id === cartItem.productId);
        const images = productImageData.filter(img => img.productId === cartItem.productId);
        const firstImage = images.length > 0 ? images[0].imageUrl : null;

        return {
          ...cartItem,
          productName: productDetails?.name || "Unknown Product",
          ornamentPrice: productDetails?.ornamentPrice || 0,
          imageUrl: firstImage,
          total: (productDetails?.ornamentPrice || 0) * cartItem.quantity,
        };
      });

      setCartItems(mergedData);
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast("Failed to load cart data", "ERROR");
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <>
      <Table
        striped
        bordered
        hover
        responsive
        className="align-middle text-center" style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        <thead className="table-dark" style={{ position: 'sticky', top: '0', zIndex: '2' }}>
          <tr>
            <th>S.No</th>
            <th>Name</th>
            <th>Email</th>
            <th>Mobile No</th>
            {/* <th>Password</th> */}
            <th>Date</th>
            <th style={{ width: "180px" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {customerData.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center text-muted">
                No customers found.
              </td>
            </tr>
          ) : (
            customerData.map((customer, index) => (
              <tr key={customer.id}>
                <td>{index + 1}</td>
                <td>{customer.name}</td>
                <td>{customer.email}</td>
                <td>{customer.phone}</td>
                {/* <td>
                  <span className="text-muted" style={{ fontSize: "0.85em" }}>
                    ••••••••
                  </span>
                </td> */}
                <td>{formatTimestamp(customer.createdTimestamp)}</td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    className="me-2"
                    onClick={() => onEdit(customer)}
                    title="Edit Customer"
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="me-2"
                    onClick={() => onDelete(customer.id)}
                    title="Delete Customer"
                  >
                    <Trash size={14} />
                  </Button>
                  <div style={{ position: 'relative' }}>
                    <Button
                      className="mt-2"
                      variant="primary"
                      size="sm"
                      onClick={() => handleViewCart(customer.id)}
                      title="View Cart"
                    >
                      View Cart
                    </Button>
                    <span style={{
                      position: 'absolute',
                      top: '0px',
                      color: 'white',
                      right: '30px',
                      padding: '2px 6px',
                      fontSize: '12px',
                      borderRadius: '50%',
                      background: 'green'
                    }}>{allCartItems[customer.id] || 0}</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <Button
                      className="mt-2"
                      variant="primary"
                      size="sm"
                      onClick={() => handleWishlist(customer.id)}
                      title="view-wishlist"
                    >
                      Wishlist
                    </Button>
                    <span style={{
                      position: 'absolute',
                      top: '0px',
                      color: 'white',
                      right: '30px',
                      padding: '2px 6px',
                      fontSize: '12px',
                      borderRadius: '50%',
                      background: 'green'
                    }}>{wishlistAllItems[customer.id] || 0}</span>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* MODAL */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCustomer
              ? `Cart for ${selectedCustomer.name}`
              : "Customer Cart"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCustomer && (
            <>
              <div className="bg-light p-3 rounded mb-4">
                <h6 className="mb-2">Customer Information</h6>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-1">
                      <strong>Name:</strong> {selectedCustomer.name}
                    </p>
                    <p className="mb-1">
                      <strong>Email:</strong> {selectedCustomer.email}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1">
                      <strong>Phone:</strong> {selectedCustomer.phone}
                    </p>
                    <p className="mb-1">
                      <strong>Registered:</strong>{" "}
                      {formatTimestamp(selectedCustomer.createdTimestamp)}
                    </p>
                  </div>
                </div>
              </div>

              <h5 className="mb-3">
                Cart Items ({cartItems.length})
                {loading && (
                  <span
                    className="ms-2 spinner-border spinner-border-sm text-primary"
                    role="status"
                  />
                )}
              </h5>

              {loading && cartItems.length === 0 ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Fetching cart items...</p>
                </div>
              ) : cartItems.length === 0 ? (
                <div className="alert alert-info text-center">
                  This customer's cart is empty.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "50px" }}>S.No</th>
                        <th style={{ width: "80px" }}>Photo</th>
                        <th>Name</th>
                        <th className="text-end" style={{ width: "120px" }}>
                          Weight (gm)
                        </th>
                        <th className="text-end" style={{ width: "140px" }}>
                          Amount (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center text-muted py-3"
                          >
                            No items in cart.
                          </td>
                        </tr>
                      ) : (
                        cartItems.map((item, index) => {
                          console.log("pric",item.priceBeforeGst);
                          const discountPrice = item.discount > 0 ? item.ornamentPrice - (item.priceBeforeGst * item.discount)/100:item.ornamentPrice;
                          return(
                          <tr key={index}>
                            <td className="align-middle text-center fw-medium">
                              {index + 1}
                            </td>
                            <td className="align-middle">
                              {item.imageUrl ? (
                                <img
                                  src={`${apiUrl}/${item.imageUrl}`}
                                  alt={item.productName}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    border: "1px solid #eee",
                                  }}
                                  onError={(e) => {
                                    e.target.src =
                                      "/assets/images/placeholder.png";
                                    e.target.style.opacity = 0.6;
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    background: "#f8f9fa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "6px",
                                    fontSize: "0.65rem",
                                    color: "#6c757d",
                                    border: "1px dashed #ddd",
                                  }}
                                >
                                  —
                                </div>
                              )}
                            </td>
                            <td className="align-middle">
                              <div className="fw-medium">
                                {item.productName}
                              </div>
                            </td>
                            <td className="align-middle text-end">
                              {parseFloat(item.weight || 0).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}{" "}
                              gm
                            </td>
                            <td className="align-middle text-end fw-bold">
                              {formatINR(Math.round(
                               discountPrice || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }))}
                            </td>
                          </tr>
                        )
                        }
                          )
                      )}
                    </tbody>
                  </Table>

                  {/* GRAND TOTAL BELOW TABLE */}
                  {cartItems.length > 0 && (
                    <div className="mt-4 p-3 bg-light border rounded d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Total Amount:</h6>
                      <h5 className="mb-0 text-success fw-bold">
                        {formatINR(Math.round(
                          cartItems.reduce(
                            (sum, item) =>{
                              const price = item.ornamentPrice || 0;
                              const qty = item.quantity ||0;
                              const discount = item.discount ||0;
                              const priceBeforeGst = item.priceBeforeGst || 0;
                              const discountPrice = discount > 0 ? price -(priceBeforeGst * discount)/100:price;
                              return sum + discountPrice * qty;

                            },0
                          )
                        ))}
                      </h5>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showWishlist}
        onHide={()=>setShowWishlist(false)}
        centered
        size="lg"
        backdrop='static'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCustomer ? `wishlist for ${selectedCustomer.name}` : 'wishlist'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCustomer && (
            <>
              <div className="bg-light p-3 rounded mb-4">
                <h6 className="mb-2">Customer Information</h6>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-1">
                      <strong>Name:</strong>{selectedCustomer.name}
                    </p>
                    <p className="mb-1">
                      <strong>Email:</strong>{selectedCustomer.email}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1">
                      <strong>Phone:</strong>{selectedCustomer.phone}
                    </p>
                  </div>
                </div>
              </div>
              <h5 className="mb-3">
                Wishlist Items({wishlistItems.length})
                {loading && (
                  <span className="ms-2 spinner-border spinner-border-sm text-primary" role="status"/>
                )}
              </h5>
              {
                loading && wishlistItems.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role='status'>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Fetch Wishlist Items...</p>
                  </div>
                ): wishlistItems.length === 0 ? (
                  <div className="alert alert-info text-center">
                    This customer's wishlist is empty.
                  </div>
                ):(
                  <div className="table-responsive">
                    <Table striped bordered hover size='sm' className="mt-3">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>ProductPhoto</th>
                          <th>ProductName</th>
                          <th>Weight</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wishlistItems.length === 0 ? (
                          <tr>
                            <td colSpan='5'
                            className="text-center text-muted py-3">
                              No items in wishlist
                            </td>
                          </tr>
                        ):(
                          wishlistItems.map((item, index) =>{
                            const discountPrice = item.discount > 0 ? item.ornamentPrice - (item.priceBeforeGst * item.discount)/100 : item.ornamentPrice;
                            return(
                            <tr key={index}>
                              <td className="align-middle text-center fw-medium">{index+1}</td>
                              <td className="align-middle">
                                {item.imageUrl ?(
                                  <img src={`${apiUrl}/${item.imageUrl}`}
                                  alt={item.productName}
                                  style={{width:'50px',
                                    height:'50px',
                                    objectFit:'cover',
                                    borderRadius:'6px',
                                    border:'1px solid #eee'
                                  }}
                                   onError={(e) => {
                                    e.target.src =
                                      "/assets/images/placeholder.png";
                                    e.target.style.opacity = 0.6;
                                  }}/>
                                ):(
                                   <div
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    background: "#f8f9fa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "6px",
                                    fontSize: "0.65rem",
                                    color: "#6c757d",
                                    border: "1px dashed #ddd",
                                  }}
                                >
                                  —
                                </div>
                                )}
                              </td>
                              <td>
                                {item.productName}
                              </td>
                              <td>
                                {parseFloat(item.weight ||0).toLocaleString(undefined,{
                                  minimumFractionDigits:2,
                                  maximumFractionDigits:2,
                                })}{''}gm
                              </td>
                              <td>
                                {formatINR(Math.round(discountPrice ||0).toLocaleString(undefined,{
                                  minimumFractionDigits:2,
                                  maximumFractionDigits:2,
                                }))
                                }
                              </td>
                            </tr>
                          )
                          }
                            )
                        )}
                      </tbody>
                    </Table>
                  </div>
                )
              }
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowWishlist(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomerList;